import { Dialog, DialogContent } from "@/components/ui/dialog";
import { fmtEUR } from "@/lib/api";
import { Phone, MapPin, User as UserIcon, StickyNote, Truck, Receipt } from "lucide-react";

/**
 * Read-only preview of a Delivery order. Surfaces customer details,
 * the full item list (with per-item comments + free text) and the
 * total. Designed to open from a «Προεπισκόπηση» button on every order
 * card/row. Clicking outside the modal closes it (Radix default).
 */
export default function DeliveryOrderPreviewDialog({ open, onOpenChange, order }) {
  if (!order) return null;

  const items = order.items || [];
  const code4 = order.code ? String(order.code).padStart(4, "0") : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-hidden"
        data-testid="delivery-preview-dialog"
      >
        {/* Header */}
        <div className="px-6 py-4 bg-slate-50 border-b-2 border-slate-200">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-heading text-2xl font-bold text-slate-900">
                {code4 ? `#${code4}` : `#${order.order_no}`}
                {code4 && (
                  <span className="ml-2 text-xs font-mono font-normal text-slate-500 align-middle">
                    Αρ.Παρ. {order.order_no}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {order.status} · {new Date(order.created_at).toLocaleString("el-GR")}
              </div>
            </div>
            <Receipt size={28} className="text-slate-400" />
          </div>
        </div>

        {/* Customer info */}
        <div className="px-6 py-4 bg-white border-b border-slate-100 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
            Πελάτης
          </div>
          {order.customer_name && (
            <div className="flex items-center gap-2 text-sm">
              <UserIcon size={14} className="text-slate-400" />
              <span className="font-bold">{order.customer_name}</span>
            </div>
          )}
          {order.phone && (
            <div className="flex items-center gap-2 text-sm font-mono">
              <Phone size={14} className="text-slate-400" />
              {order.phone}
            </div>
          )}
          {order.address && (
            <div className="flex items-start gap-2 text-sm">
              <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <span>{order.address}</span>
            </div>
          )}
          {order.notes && (
            <div className="flex items-start gap-2 text-sm italic text-slate-700">
              <StickyNote size={14} className="text-amber-500 mt-0.5 shrink-0" />
              <span>{order.notes}</span>
            </div>
          )}
          {order.driver_name && (
            <div className="flex items-center gap-2 text-sm">
              <Truck size={14} className="text-blue-500" />
              <span>{order.driver_name}</span>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="px-6 py-3 bg-white max-h-[50vh] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
            Είδη ({items.length})
          </div>
          <div className="space-y-2">
            {items.map((it, i) => {
              const qty = Number(it.qty || 1);
              const price = Number(it.price || 0);
              const sub = qty * price;
              const cArr = Array.isArray(it.comments) ? it.comments : [];
              return (
                <div key={i} className="border-b border-slate-100 pb-2 last:border-b-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-bold text-slate-800 text-sm">
                      <span className="text-slate-500 font-mono">{qty} ×</span> {it.description}
                    </div>
                    <div className="font-mono text-emerald-700 font-bold text-sm shrink-0">
                      {fmtEUR(sub)}
                    </div>
                  </div>
                  {qty > 1 && (
                    <div className="text-[11px] text-slate-500 font-mono pl-5">
                      {qty} × {fmtEUR(price)}
                    </div>
                  )}
                  {cArr.length > 0 && (
                    <div className="text-xs italic text-slate-600 pl-5 mt-0.5">
                      · {cArr.map((c) => c.label || c.name || "").join(", ")}
                    </div>
                  )}
                  {it.free_text && (
                    <div className="text-xs italic text-slate-600 pl-5 mt-0.5">
                      · {it.free_text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Total */}
        <div className="px-6 py-3 bg-slate-50 border-t-2 border-slate-200 flex items-center justify-between">
          <span className="font-bold text-slate-800">ΣΥΝΟΛΟ</span>
          <span className="font-mono font-black text-xl text-emerald-700">
            {fmtEUR(order.total || 0)}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
